import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { AuthProvider, useAuth } from './lib/auth';
import { Layout } from './components/layout/Layout';
import { Toaster } from './components/ui/toaster';
import { LoginPage } from './pages/LoginPage';
import { TwoFactorVerifyPage } from './pages/TwoFactorVerifyPage';
import { TwoFactorSetupPage } from './pages/TwoFactorSetupPage';
import { DashboardPage } from './pages/DashboardPage';
import { PartnerAccountsPage } from './pages/PartnerAccountsPage';
import { PartnerAccountDetailPage } from './pages/PartnerAccountDetailPage';
import { SyncedLocationsPage } from './pages/SyncedLocationsPage';
import { SyncedLocationDetailPage } from './pages/SyncedLocationDetailPage';
import { BookingPushLogsPage } from './pages/BookingPushLogsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { DuplicatesPage } from './pages/duplicates/DuplicatesPage';
import { BatchOperationsPage } from './pages/BatchOperationsPage';

export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading, sessionExpired, signedOut, storageError } = useAuth();
  const location = useLocation();

  // T044m: Handle browser storage unavailable
  if (storageError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-destructive mb-2">Storage Unavailable</h1>
          <p className="text-muted-foreground">{storageError}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // T044k/FR-001e: the login form shows the session expiry message and clears the flag. It is not
    // cleared here: a state change during this render would re-run <Navigate> with new state.
    // FR-001f: sign-in returns to this page, its query (list page, filters) included; not after Logout.
    const from = signedOut ? undefined : `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ sessionExpired, from }} />;
  }

  return <>{children}</>;
}

function SettingsPage(): JSX.Element {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Settings</h1>
      <p className="text-muted-foreground">
        Settings page coming soon.
      </p>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/2fa/verify" element={<TwoFactorVerifyPage />} />
          <Route path="/2fa/setup" element={<TwoFactorSetupPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/partner-accounts" element={<PartnerAccountsPage />} />
                    <Route path="/partner-accounts/:id" element={<PartnerAccountDetailPage />} />
                    <Route path="/synced-locations" element={<SyncedLocationsPage />} />
                    <Route path="/synced-locations/:id" element={<SyncedLocationDetailPage />} />
                    <Route path="/booking-push-logs" element={<BookingPushLogsPage />} />
                    <Route path="/duplicates" element={<DuplicatesPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="/batch-operations" element={<BatchOperationsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}
